import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX, type PropsWithChildren } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Role, useAuth } from '@invite/app-core';
import { isNotEnabled } from '../api/errors';
import { getMe, listUnits, switchTeam } from '../api/ownerApi';
import { ownerKeys } from '../api/queryKeys';
import type { OwnedUnit } from '../api/schemas';
import { AuthLayout } from '../app/AuthLayout';
import { useT } from '../i18n/useT';
import { isOwnerToken } from './ownerToken';
import { NoUnitsScreen } from './NoUnitsScreen';
import { NotEnabledScreen } from './NotEnabledScreen';

export const GateError = (): JSX.Element => {
  const t = useT();
  return (
    <AuthLayout>
      <p role="alert">{t('Something went wrong. Please try again.')}</p>
      <Link className="ow-link" to="/logout">
        {t('Sign out')}
      </Link>
    </AuthLayout>
  );
};

export const Pending = (): JSX.Element => {
  const t = useT();
  return (
    <AuthLayout>
      <p className="ow-muted" role="status">
        {t('Loading…')}
      </p>
    </AuthLayout>
  );
};

/** The first listed unit whose team is not in `avoid`, in the order the server gave them. */
const firstOutside = (units: OwnedUnit[], avoid: ReadonlySet<number>): OwnedUnit | undefined =>
  units.find((u) => !avoid.has(u.teamId));

/**
 * Swap the session to a token for `teamId` that carries the owner role. `left` records the team
 * being switched away from, so the gate never switches back to a team that already failed.
 */
const SwitchTeam = ({
  teamId,
  fromTeamId,
  left
}: {
  teamId: number;
  fromTeamId: number | null;
  left: Set<number>;
}): JSX.Element => {
  const { signIn } = useAuth();
  const started = useRef(false);
  const [failure, setFailure] = useState<unknown>(null);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    if (fromTeamId !== null) {
      left.add(fromTeamId);
    }
    void switchTeam(teamId).then(
      (login) => {
        const isOwner = isOwnerToken(login.token);
        if (!isOwner) {
          setFailure(new Error('switch-team returned a token without the owner role'));
          return;
        }
        signIn(login.token, new Date(login.tokenExpiryDate));
      },
      (caught: unknown) => {
        setFailure(caught);
      }
    );
  }, [teamId, fromTeamId, left, signIn]);

  if (isNotEnabled(failure)) {
    return <NotEnabledScreen />;
  }
  if (failure !== null) {
    return <GateError />;
  }
  return <Pending />;
};

/** A staff session: find the first owned team and swap to a token that carries the owner role. */
const SwitchToOwner = ({
  fromTeamId,
  left
}: {
  fromTeamId: number | null;
  left: Set<number>;
}): JSX.Element => {
  const units = useQuery({ queryKey: ownerKeys.units, queryFn: listUnits, retry: false });
  const first = units.data?.[0];

  if (units.data?.length === 0) {
    return <NoUnitsScreen />;
  }
  if (units.isError) {
    return <GateError />;
  }
  if (first === undefined) {
    return <Pending />;
  }
  return <SwitchTeam teamId={first.teamId} fromTeamId={fromTeamId} left={left} />;
};

/**
 * The owner's own token is in place; `/owner/me` doubles as the "is Owner turned on" probe. The
 * owned-units list is consulted too: a token whose team lists no unit (Owner off there, or no
 * unit owned there), or whose `/owner/me` answers 403 while other teams are listed, is switched
 * to the first listed team it has not already left.
 */
const OwnerReady = ({
  teamId,
  left,
  children
}: PropsWithChildren<{ teamId: number | null; left: Set<number> }>): JSX.Element => {
  const units = useQuery({ queryKey: ownerKeys.units, queryFn: listUnits, retry: false });
  const me = useQuery({ queryKey: ownerKeys.me(teamId), queryFn: getMe, retry: false });
  const refused = isNotEnabled(me.error);
  const listed = units.data;

  if (listed !== undefined && listed.length > 0) {
    const inTeam = listed.some((u) => u.teamId === teamId);
    if (!inTeam || refused) {
      const avoid = new Set(left);
      if (teamId !== null) {
        avoid.add(teamId);
      }
      const target = firstOutside(listed, avoid);
      if (target !== undefined) {
        return (
          <SwitchTeam
            key={`${String(teamId)}:${String(target.teamId)}`}
            teamId={target.teamId}
            fromTeamId={teamId}
            left={left}
          />
        );
      }
      if (!inTeam && !refused) {
        return <GateError />;
      }
    }
  }
  if (refused) {
    return <NotEnabledScreen />;
  }
  // A failed refetch keeps the profile already loaded; the booking sheet handles it there.
  if (me.isError && me.data === undefined) {
    return <GateError />;
  }
  // A units error is left to the unit provider, which shows the error screen itself.
  if (me.data === undefined || units.isPending) {
    return <Pending />;
  }
  return <>{children}</>;
};

export const RequireOwner = ({ children }: PropsWithChildren): JSX.Element => {
  const { token, principal, bootstrapped } = useAuth();
  const location = useLocation();
  // Teams this gate has switched away from; lives as long as the gate, across token changes.
  const [left] = useState(() => new Set<number>());
  if (!bootstrapped) {
    return <Pending />;
  }
  if (token === null || principal === null) {
    const returnUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  if (principal.role !== Role.OWNER) {
    return <SwitchToOwner fromTeamId={principal.teamId} left={left} />;
  }
  return (
    <OwnerReady teamId={principal.teamId} left={left}>
      {children}
    </OwnerReady>
  );
};
